import { SQDCEventActionTypeInternalType, SQDCEventActionTypeType } from "@/graphql";
import { checkId } from "@/mixins/redirectToBoard";
import type { RouteRecordRaw } from "vue-router";
import { parseNumberQuery, parseTimeQuery } from "@/modules/sqdc/helper/DataMaintenanceRouteHelper";

const router: RouteRecordRaw[] = [
  {
    path: "/sqdc/type-list/:internalType",
    name: "SQDCEventActionTypeList",
    component: () => import("./views/SQDCEventActionTypeList.vue"),
    beforeEnter: (to) =>
      Object.values(SQDCEventActionTypeInternalType).includes(
        to.params.internalType as SQDCEventActionTypeInternalType,
      )
        ? true
        : { name: "pageNotFound" },
    props: (route) => ({
      internalType: route.params.internalType as SQDCEventActionTypeInternalType,
      listName: typeof route.query.list === "string" ? route.query.list : undefined,
      archivedOnly: route.query.archive === "true",
    }),
  },
  {
    path: "/sqdc/EABShorthandList",
    name: "EABShorthandList",
    component: () => import("./views/EABShorthandActions.vue"),
  },
  {
    path: "/sqdc/createEABShorthand",
    name: "CreateEABShorthand",
    component: () => import("./views/CreateEABShorthand.vue"),
  },
  {
    path: "/sqdc/updateEABShorthand/:id",
    name: "UpdateEABShorthand",
    component: () => import("./views/UpdateEABShorthand.vue"),
    props: (route) => {
      return {
        shorthandId: parseInt(route.params.id.toString(), 10),
      };
    },
  },
  {
    path: "/sqdc/dataMaintenanceNew",
    name: "DataMaintenanceNew",
    component: () => import("./views/DataMaintenanceNew.vue"),
    props: (route) => ({
      initialCostCenterIds: parseNumberQuery(route.query.costCenterIds),
      initialDatapointTypeIds: parseNumberQuery(route.query.datapointTypeIds),
      initialTime: parseTimeQuery(route.query.time),
      initialSiteId: parseNumberQuery(route.query.siteId)[0],
    }),
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/boardManagement/:boardId?",
    name: "boardManagement",
    component: () => import("./views/BoardManagement.vue"),
    meta: {
      rights: ["sqdc.admin"],
    },
    props: (route) => {
      return {
        boardId: parseInt(route.params.boardId.toString(), 10),
      };
    },
    beforeEnter: (to) =>
      checkId(to.name, Number(to.params.boardId?.toString()), true, false, "sqdc.admin"),
  },
  {
    path: "/flags",
    name: "flags",
    component: () => import("../flags/views/FlagList.vue"),
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/updateFlag/:id(\\d+)",
    name: "UpdateFlag",
    component: () => import("../flags/views/UpdateFlag.vue"),
    meta: {
      rights: ["sqdc.admin"],
    },
    props: (route) => ({ id: Number(route.params.id) }),
  },
  {
    path: "/createFlag",
    name: "CreateFlag",
    component: () => import("../flags/views/CreateFlag.vue"),
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/mailDistributions",
    name: "mailDistributions",
    component: () => import("./components/mailDistribution/MailDistributions.vue"),
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/mailDistribution/:id",
    name: "mailDistributionUpdate",
    component: () => import("../flags/views/UpdateFlag.vue"),
    meta: {
      rights: ["sqdc.admin"],
    },
    props: (route) => {
      return {
        id: parseInt(route.params.id.toString(), 10),
        context: "MailDistribution",
      };
    },
  },
  {
    path: "/mailDistributionCreate/:siteId",
    name: "mailDistributionCreate",
    component: () => import("../flags/views/CreateFlag.vue"),
    meta: {
      rights: ["sqdc.admin"],
    },
    props: (route) => {
      return {
        siteId: parseInt(route.params.siteId.toString(), 10),
        context: "MailDistribution",
      };
    },
  },
  {
    path: "/sqdc",
    name: "SQDC",
    redirect: { name: "SQDCBoard" },
    children: [
      {
        path: "eventActionConfig",
        name: "EventActionConfig",
        meta: {
          rights: ["sqdc.admin"],
        },
        redirect: { name: "EventActionConfigEvent" },
        children: [
          {
            path: "event",
            name: "EventActionConfigEvent",
            meta: {
              rights: ["sqdc.admin"],
            },
            redirect: { name: "EventActionConfigEventOverview" },
            children: [
              {
                path: "create",
                name: "EventActionConfigEventCreate",
                component: () =>
                  import("./components/eventActionConfig/event/SQDCEventConfigCreate.vue"),
                meta: {
                  rights: ["sqdc.admin"],
                },
              },
              {
                path: "overview",
                name: "EventActionConfigEventOverview",
                props: () => {
                  return { type: SQDCEventActionTypeType.EVENT };
                },
                component: () =>
                  import("./components/eventActionConfig/SQDCEventActionConfigOverview.vue"),
                meta: {
                  rights: ["sqdc.admin"],
                },
              },
              {
                path: ":id",
                name: "EventActionConfigEventUpdate",
                component: () =>
                  import("./components/eventActionConfig/SQDCEventActionConfigUpdate.vue"),
                meta: {
                  rights: ["sqdc.admin"],
                },
                props: (route) => {
                  return {
                    id: parseInt(route.params.id.toString(), 10),
                  };
                },
              },
            ],
          },
          {
            path: "action",
            name: "EventActionConfigAction",
            meta: {
              rights: ["sqdc.admin"],
            },
            redirect: { name: "EventActionConfigActionOverview" },
            children: [
              {
                path: "create",
                name: "EventActionConfigActionCreate",
                component: () =>
                  import("./components/eventActionConfig/action/SQDCActionConfigCreate.vue"),
                meta: {
                  rights: ["sqdc.admin"],
                },
              },
              {
                path: "overview",
                name: "EventActionConfigActionOverview",
                props: () => {
                  return { type: SQDCEventActionTypeType.ACTION };
                },
                component: () =>
                  import("./components/eventActionConfig/SQDCEventActionConfigOverview.vue"),
                meta: {
                  rights: ["sqdc.admin"],
                },
              },
              {
                path: ":id",
                name: "EventActionConfigActionUpdate",
                component: () =>
                  import("./components/eventActionConfig/SQDCEventActionConfigUpdate.vue"),
                meta: {
                  rights: ["sqdc.admin"],
                },
                props: (route) => {
                  return {
                    id: parseInt(route.params.id.toString(), 10),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: "workflow",
        name: "SQDCWorkflows",
        meta: { rights: ["sqdc.admin"] },
        redirect: { name: "SQDCWorkflowList" },
        children: [
          {
            path: "list",
            name: "SQDCWorkflowList",
            meta: { rights: ["sqdc.admin"] },
            component: () => import("./components/workflow/SQDCWorkflowOverview.vue"),
          },
          {
            path: "create",
            name: "SQDCWorkflowCreate",
            meta: { rights: ["sqdc.admin"] },
            component: () => import("./components/workflow/SQDCWorkflowEditView.vue"),
          },
          {
            path: "edit/:id",
            name: "SQDCWorkflowEdit",
            meta: { rights: ["sqdc.admin"] },
            component: () => import("./components/workflow/SQDCWorkflowEditView.vue"),
            props: (route) => ({
              id: typeof route.params.id === "string" ? route.params.id : undefined,
            }),
          },
          {
            path: "shorthand",
            name: "SQDCShorthand",
            meta: { rights: ["sqdc.admin"] },
            redirect: { name: "SQDCShorthandList" },
            children: [
              {
                path: "list",
                meta: { rights: ["sqdc.admin"] },
                name: "SQDCShorthandList",
                redirect: {
                  name: "EventActionConfigEventOverview",
                  query: { tab: "shorthand" },
                },
              },
              {
                path: "create",
                meta: { rights: ["sqdc.admin"] },
                name: "SQDCShorthandCreate",
                component: () => import("./components/EABWokflow/EABShorthandCreate.vue"),
                props: (route) => ({
                  eventActionTypeTypes:
                    route.query.type === SQDCEventActionTypeType.EVENT ||
                    route.query.type === SQDCEventActionTypeType.ACTION
                      ? [route.query.type]
                      : undefined,
                }),
              },
              {
                path: ":id",
                name: "SQDCShorthandUpdate",
                meta: { rights: ["sqdc.admin"] },
                component: () => import("./components/EABWokflow/EABShorthandUpdate.vue"),
                props: (route) => {
                  return {
                    id: parseInt(route.params.id.toString(), 10),
                    type:
                      route.query.type === SQDCEventActionTypeType.EVENT ||
                      route.query.type === SQDCEventActionTypeType.ACTION
                        ? route.query.type
                        : undefined,
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: "configuration",
        name: "SQDCConfiguration",
        component: () => import("./views/SQDCConfiguration.vue"),
        meta: {
          rights: ["systemSettings.admin"],
        },
      },
    ],
  },

  {
    path: "/sqdcBoard",
    name: "SQDCBoard",
    component: () => import("@/modules/sqdc/views/SQDCBoard.vue"),
    meta: {
      rights: ["sqdc.read"],
    },
    props: (route) => {
      return {
        boardId: route.params?.boardId ? Number(route.params.boardId) : undefined,
        transitionName: route.name === "EventActionBoardNew" ? "slide-right" : "slide-left",
      };
    },
    redirect: (to) => ({
      name: "KPIBoardNew",
      params: { boardId: to.params.boardId ?? -1 },
    }),

    children: [
      {
        path: "kpi/:boardId(\\d+)?",
        name: "KPIBoardNew",
        meta: {
          supportsMonitorView: true,
        },
        component: () => import("@/modules/sqdc/views/KPIBoard.vue"),
        beforeEnter: (to) =>
          checkId(to.name, Number(to.params.boardId?.toString()), undefined, true),
        props: (route) => ({
          boardId: Number(route.params.boardId),
        }),
      },
      {
        path: "eab/:boardId(\\d+)?",
        name: "EventActionBoardNew",
        component: () => import("@/modules/sqdc/views/EventActionBoardRoute.vue"),
        beforeEnter: (to) =>
          checkId(to.name, Number(to.params.boardId?.toString()), undefined, true),
        meta: {
          supportsMonitorView: true,
        },
      },
      {
        path: "eabNew/:boardId(\\d+)?",
        name: "EventActionBoardNewNew",
        redirect: (to) => ({
          name: "EventActionBoardNew",
          params: to.params,
          query: to.query,
          hash: to.hash,
        }),
      },
    ],
  },
  {
    path: "/taskboard/:id?",
    name: "Taskboard",
    redirect: (to) => ({
      name: "EventActionBoardNew",
      params: { boardId: to.params.id },
    }),
  },
  {
    path: "/kpiboard/:id?",
    name: "KPIBoard",
    redirect: (to) => ({
      name: "KPIBoardNew",
      params: { boardId: to.params.id ?? 1 },
    }),
  },
];

export default router;
